import { cn } from '@/lib/utils';

const MAX_SEGMENTS = 24;

/**
 * Segmented progress for an installment plan. `billed` is how many parcelas the fatura calendar
 * has already charged — NOT how many were paid: a parcela joins the open invoice and is only
 * settled when that invoice is, which is why `resolveRecurringPaymentStatus` calls a card-backed
 * charge `na-fatura` and never `paga`. Falls back to a single proportional bar when the plan has
 * more installments than we can render as ticks.
 */
export function InstallmentTrack({
  billed,
  total,
  className,
}: {
  billed: number;
  total: number;
  className?: string;
}) {
  const safeTotal = Math.max(total, 1);
  const safeBilled = Math.min(Math.max(billed, 0), safeTotal);
  const ariaLabel = `${safeBilled} de ${safeTotal} parcelas lançadas`;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {safeTotal <= MAX_SEGMENTS ? (
        <div className="flex flex-1 gap-[2.5px]" role="img" aria-label={ariaLabel}>
          {Array.from({ length: safeTotal }).map((_, i) => (
            <span
              key={i}
              className={cn('h-1 flex-1 rounded-full', i < safeBilled ? 'bg-warning' : 'bg-muted')}
            />
          ))}
        </div>
      ) : (
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted" role="img" aria-label={ariaLabel}>
          <div
            className="h-full rounded-full bg-warning"
            style={{ width: `${(safeBilled / safeTotal) * 100}%` }}
          />
        </div>
      )}
      <span className="shrink-0 font-mono text-3xs text-text-secondary tabular-nums">
        {safeBilled}/{safeTotal}
      </span>
    </div>
  );
}
